import { useEffect, useMemo, useRef, useState } from 'react';
import { API } from '../service/ProductsService';
import ProductRow from './ProductRow';
import SearchBox from './SearchBox';

const EXAMPLES = ['דבש', 'חלב תנובה', 'במבה', 'חלה'];

// רשימת ההשלמה נשמרת בדפדפן, כדי שכניסה חוזרת לאתר לא תטען אותה שוב
const SUGGESTIONS_KEY = 'search-suggestions';
const SUGGESTIONS_TTL_MS = 6 * 60 * 60 * 1000;

const readCachedSuggestions = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(SUGGESTIONS_KEY));
    if (cached && Date.now() - cached.savedAt < SUGGESTIONS_TTL_MS) return cached;
  } catch (e) { /* localStorage חסום / לא תקין — פשוט טוענים מהשרת */ }
  return null;
};

const toItems = ({ names = [], brands = [] }) => [
  ...names.map((text) => ({ text, lower: text.toLowerCase(), type: 'product' })),
  ...brands.map((text) => ({ text, lower: text.toLowerCase(), type: 'brand' })),
];

const s = {
  searchBar: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  button:    { padding: '10px 20px', fontSize: 16, fontWeight: 600, border: 'none', borderRadius: 10, background: '#1a3f9c', color: '#fff' },
  select:    { padding: '10px 12px', border: '1px solid #d0d7de', borderRadius: 10, background: '#fff' },
  error:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, background: '#fff1f0', border: '1px solid #ffccc7', color: '#a8071a', padding: '10px 14px', borderRadius: 10, marginBottom: 16 },
  errorX:    { background: 'none', border: 'none', color: 'inherit', fontSize: 18, lineHeight: 1 },
  summary:   { display: 'flex', flexWrap: 'wrap', gap: '4px 16px', color: '#59636e', marginBottom: 12, fontSize: 14 },
  list:      { display: 'flex', flexDirection: 'column', gap: 10 },
  empty:     { textAlign: 'center', color: '#59636e', padding: '48px 16px', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' },
  chips:     { display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 12 },
  chip:      { padding: '6px 12px', borderRadius: 999, border: '1px solid #d0d7de', background: '#fff', color: '#1f2328' },
};

const SearchTab = () => {
  const [q, setQ] = useState('');                    // מה שמוקלד בשדה
  const [searched, setSearched] = useState('');      // החיפוש שבוצע בפועל
  const [sort, setSort] = useState('relevance');
  const [suggestionItems, setSuggestionItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState(null);          // null = עוד לא חיפשו
  const [pricesByItem, setPricesByItem] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestId = useRef(0);                       // מונע מתשובה ישנה לדרוס תשובה חדשה

  // טעינה חד-פעמית של שמות המוצרים והחברות להשלמה האוטומטית
  useEffect(() => {
    const cached = readCachedSuggestions();
    if (cached) {
      setSuggestionItems(toItems(cached));
      return;
    }
    API.getSearchSuggestions()
      .then((resp) => {
        if (resp?.[0]?.[0]?.code !== 0) return;
        const data = {
          savedAt: Date.now(),
          names: (resp[1] || []).map((r) => r.name),
          brands: (resp[2] || []).map((r) => r.brandName),
        };
        setSuggestionItems(toItems(data));
        try { localStorage.setItem(SUGGESTIONS_KEY, JSON.stringify(data)); } catch (e) { /* אין מקום / חסום */ }
      })
      .catch(() => { /* בלי השלמה אוטומטית החיפוש עדיין עובד */ });
  }, []);

  const load = async (text, sortBy) => {
    const query = text.trim();
    if (query.length < 2) {
      setError('נא להזין לפחות 2 תווים לחיפוש');
      return;
    }
    const id = ++requestId.current;
    setSearched(query);
    setLoading(true);
    try {
      const resp = await API.searchProducts(query, sortBy);
      if (id !== requestId.current) return;
      const status = resp?.[0]?.[0];
      if (status?.code === 0) {
        // [1] stores, [2] items, [3] prices (ר' spSearchProducts ב-server/src/SQL/Products.sql)
        const byItem = {};
        (resp[3] || []).forEach((p) => {
          if (!byItem[p.itemId]) byItem[p.itemId] = {};
          byItem[p.itemId][p.storeId] = p;
        });
        setStores(resp[1] || []);
        setItems(resp[2] || []);
        setPricesByItem(byItem);
        setError(null);
      } else {
        setError(status?.msg || 'שגיאה בחיפוש');
      }
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err.message || 'שגיאה בחיפוש');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  const search = (text) => load(text, sort);

  // שינוי מיון מריץ שוב את החיפוש האחרון (לא את מה שמוקלד כרגע)
  const changeSort = (value) => {
    setSort(value);
    if (searched) load(searched, value);
  };

  const runExample = (text) => {
    setQ(text);
    search(text);
  };

  // באיזה סופר זול יותר, מתוך המוצרים המוצגים שקיימים ביותר מסופר אחד
  const cheaperCounts = useMemo(() => {
    const counts = {};
    (items || []).forEach((item) => {
      if (item.cheapestStoreId) counts[item.cheapestStoreId] = (counts[item.cheapestStoreId] || 0) + 1;
    });
    return stores.map((st) => ({ ...st, count: counts[st.id] || 0 })).filter((st) => st.count > 0);
  }, [items, stores]);

  const totalCount = items?.[0]?.totalCount ?? 0;

  return (
    <div>
      <div style={s.searchBar}>
        <SearchBox value={q} onChange={setQ} onSubmit={search} items={suggestionItems} />
        <button style={s.button} onClick={() => search(q)} disabled={loading}>חיפוש</button>
        <select style={s.select} value={sort} onChange={(e) => changeSort(e.target.value)} aria-label="מיון">
          <option value="relevance">מיון: רלוונטיות</option>
          <option value="diff">מיון: הפרש מחיר גדול</option>
        </select>
      </div>

      {error && (
        <div style={s.error}>
          <span>{error}</span>
          <button style={s.errorX} onClick={() => setError(null)} aria-label="סגירה">×</button>
        </div>
      )}

      {items === null && !loading && (
        <div style={s.empty}>
          <div>הקלידו שם מוצר או שם חברה ולחצו חיפוש, כדי לראות את המחיר בכל סופר</div>
          <div style={s.chips}>
            {EXAMPLES.map((ex) => (
              <button key={ex} style={s.chip} onClick={() => runExample(ex)}>{ex}</button>
            ))}
          </div>
        </div>
      )}

      {items === null && loading && <div style={s.empty}>מחפש…</div>}

      {items !== null && (
        <>
          <div style={s.summary}>
            <span>
              {loading ? 'מחפש…' : `נמצאו ${totalCount} מוצרים עבור "${searched}"`}
              {!loading && totalCount > items.length ? ` (מוצגים ${items.length} הראשונים)` : ''}
            </span>
            {cheaperCounts.map((st) => (
              <span key={st.id}>זול יותר ב{st.name}: {st.count}</span>
            ))}
          </div>

          {items.length === 0 ? (
            <div style={s.empty}>לא נמצאו מוצרים עבור "{searched}"</div>
          ) : (
            <div style={s.list}>
              {items.map((item) => (
                <ProductRow key={item.id} item={item} stores={stores} prices={pricesByItem[item.id] || {}} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SearchTab;
